// @spec BCP-STR-036
'use client';

import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight, MapPin, Plus } from 'lucide-react';
import * as React from 'react';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { useAlternateSitesList } from '@/features/bcp/api/resources';
import { AlternateSiteTable } from '@/features/bcp/components/AlternateSiteTable';
import { RecoveryResourceEmptyState } from '@/features/bcp/components/RecoveryResourceEmptyState';
import type { AlternateSiteFiltersState, AlternateSiteSortField } from '@/features/bcp/types/resources';
import { useOrganizations } from '@/hooks/use-organizations';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

const sitesSearchSchema = z.object({
  page: z.coerce.number().optional().default(1),
  pageSize: z.coerce.number().optional().default(20),
  siteType: z.enum(['hot_site', 'warm_site', 'cold_site']).optional(),
  status: z.enum(['available', 'partially_available', 'unavailable']).optional(),
  search: z.string().optional(),
  sortBy: z
    .enum(['name', 'site_type', 'status', 'last_verification_date'])
    .optional()
    .default('name'),
  sortOrder: z.enum(['asc', 'desc']).optional().default('asc'),
});

export const Route = createFileRoute('/_authenticated/bcp/resources/sites')({
  validateSearch: sitesSearchSchema,
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.alternateSites'),
  }),
  component: AlternateSitesListPage,
});

function AlternateSitesListPage() {
  const { t } = useTranslation(['bcp', 'common']);
  const navigate = useNavigate();
  const search = useSearch({ from: '/_authenticated/bcp/resources/sites' });
  const { organizations, isLoading: orgsLoading } = useOrganizations();

  const activeOrg = organizations.find((org) => org.isActive);

  const filters: AlternateSiteFiltersState = React.useMemo(
    () => ({
      siteType: search.siteType ?? null,
      status: search.status ?? null,
      search: search.search ?? null,
      sortBy: search.sortBy as AlternateSiteSortField,
      sortOrder: search.sortOrder,
    }),
    [search],
  );

  const {
    data: sitesData,
    isLoading: sitesLoading,
    isFetching: sitesFetching,
  } = useAlternateSitesList({
    page: search.page,
    pageSize: search.pageSize,
    ...filters,
    enabled: Boolean(activeOrg),
  });

  const handleSort = React.useCallback(
    (field: AlternateSiteSortField) => {
      const newOrder = filters.sortBy === field && filters.sortOrder === 'asc' ? 'desc' : 'asc';
      void navigate({
        search: { ...search, sortBy: field, sortOrder: newOrder, page: 1 } as never,
        replace: true,
      });
    },
    [navigate, search, filters],
  );

  const handleRowClick = React.useCallback(
    (siteId: string) => {
      void navigate({
        to: '/bcp/resources/sites/$siteId',
        params: { siteId },
      });
    },
    [navigate],
  );

  const handlePageChange = React.useCallback(
    (page: number) => {
      void navigate({
        search: { ...search, page } as never,
        replace: true,
      });
    },
    [navigate, search],
  );

  if (orgsLoading) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-7xl">
          <div className="animate-pulse space-y-4">
            <div className="h-8 w-48 rounded bg-muted" />
            <div className="h-12 rounded bg-muted" />
            <div className="h-64 rounded bg-muted" />
          </div>
        </div>
      </div>
    );
  }

  if (!activeOrg) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-7xl">
          <div className="rounded-lg border border-dashed p-12 text-center">
            <h2 className="text-lg font-semibold text-foreground">{t('common:routeErrors.noOrgSelected')}</h2>
            <p className="mt-2 text-muted-foreground">
              {t('common:routeErrors.noOrgSelectedDesc')}
            </p>
            <Button
              variant="outline"
              onClick={() => void navigate({ to: '/settings/organizations' })}
              className="mt-4"
            >
              {t('common:routeErrors.goToOrganizations')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const pagination = sitesData?.pagination;
  const totalPages = pagination?.totalPages ?? 1;
  const currentPage = pagination?.page ?? 1;

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-7xl space-y-6">
        {/* Header */}
        <header className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <MapPin className="h-8 w-8 text-primary" />
            <div>
              <h1 className="text-3xl font-semibold text-foreground">Alternate Sites</h1>
              <p className="mt-1 text-muted-foreground">
                Manage alternate recovery sites for {activeOrg.name}
              </p>
            </div>
          </div>
          <Button
            onClick={() =>
              void navigate({ to: '/bcp/resources/sites/$siteId', params: { siteId: 'new' } })
            }
          >
            <Plus className="mr-1 h-4 w-4" />
            {t('bcp:routes.addSite')}
          </Button>
        </header>

        {/* Content */}
        {sitesLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-16 animate-pulse rounded-lg bg-muted" />
            ))}
          </div>
        ) : sitesData?.items.length === 0 ? (
          <RecoveryResourceEmptyState hasFilters={false} onClearFilters={() => undefined} />
        ) : (
          <>
            <AlternateSiteTable
              sites={sitesData?.items ?? []}
              sortBy={filters.sortBy}
              sortOrder={filters.sortOrder}
              onSortChange={handleSort}
              onRowClick={handleRowClick}
            />

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t pt-4">
                <p className="text-sm text-muted-foreground">
                  Page {currentPage} of {totalPages} ({pagination?.totalItems ?? 0} sites)
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      handlePageChange(currentPage - 1);
                    }}
                    disabled={currentPage <= 1 || sitesFetching}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      handlePageChange(currentPage + 1);
                    }}
                    disabled={currentPage >= totalPages || sitesFetching}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
